import React, { useState, useEffect, useRef } from 'react';
import {
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { adminService, Payment } from '../../services/admin.service';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import SideModal from '../../components/shared/SideModal/SideModal';
import { useTranslation } from 'react-i18next';

interface PaymentCardProps {
  payment: Payment;
  onMarkPaid: (paymentId: string) => Promise<void>;
  onViewDetails: (payment: Payment) => void;
  isLoading?: boolean;
}

const PaymentCard: React.FC<PaymentCardProps> = ({
  payment,
  onMarkPaid,
  onViewDetails,
  isLoading = false,
}) => {
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PAID':
        return <CheckCircleOutlined className="text-green-600 dark:text-green-400" />;
      case 'PENDING':
        return <ClockCircleOutlined className="text-yellow-600 dark:text-yellow-400" />;
      case 'FAILED':
        return <CloseCircleOutlined className="text-red-600 dark:text-red-400" />;
      case 'OVERDUE':
        return <CloseCircleOutlined className="text-orange-600 dark:text-orange-400" />;
      default:
        return <ClockCircleOutlined className="text-gray-600 dark:text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      case 'PENDING':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700';
      case 'FAILED':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      case 'OVERDUE':
        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-700';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-600';
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'N/A';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleMarkPaid = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMarkingPaid(true);
    try {
      await onMarkPaid(payment.paymentId);
    } finally {
      setIsMarkingPaid(false);
    }
  };

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onViewDetails(payment)}
    >
      {/* Header: Cliente y Monto */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Cliente</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {payment.tenantId}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Monto</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {payment.amount?.toFixed(2)} {payment.currency || 'USD'}
          </p>
        </div>
      </div>

      {/* Status y Método */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
        <span
          className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(
            payment.status
          )}`}
        >
          {getStatusIcon(payment.status)}
          {payment.status}
        </span>
      </div>

      {/* Fechas */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">Fecha de vencimiento:</span>
          <span className="text-gray-900 dark:text-white font-medium">
            {formatDate(payment.dueDate)}
          </span>
        </div>
        {payment.paidDate && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Fecha de pago:</span>
            <span className="text-gray-900 dark:text-white font-medium">
              {formatDate(payment.paidDate)}
            </span>
          </div>
        )}
      </div>

      {/* Acciones */}
      {payment.status === 'PENDING' && (
        <button
          onClick={handleMarkPaid}
          disabled={isMarkingPaid || isLoading}
          className="w-full mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
        >
          {isMarkingPaid ? 'Marcando...' : 'Marcar como pagado'}
        </button>
      )}
    </div>
  );
};

interface PaymentDetailsModalProps {
  payment: Payment | null;
  isOpen: boolean;
  onClose: () => void;
}

const PaymentDetailsModal: React.FC<PaymentDetailsModalProps> = ({
  payment,
  isOpen,
  onClose,
}) => {
  if (!payment) return null;

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'N/A';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <SideModal isOpen={isOpen} onClose={onClose} title="Detalles del pago">
      <div className="space-y-6">
        {/* Payment ID */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">ID de pago</p>
          <p className="text-lg font-mono text-gray-900 dark:text-white break-all">
            {payment.paymentId}
          </p>
        </div>

        {/* Tenant ID */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Cliente (Tenant ID)</p>
          <p className="text-lg font-mono text-gray-900 dark:text-white break-all">
            {payment.tenantId}
          </p>
        </div>

        {/* Monto */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-1">Monto</p>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {payment.amount?.toFixed(2)}
            </p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
            <p className="text-sm text-purple-700 dark:text-purple-300 mb-1">Moneda</p>
            <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
              {payment.currency || 'USD'}
            </p>
          </div>
        </div>

        {/* Status */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Estado</p>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-700">
              {payment.status}
            </span>
          </div>
        </div>

        {/* Dates */}
        <div className="space-y-3">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Fecha de vencimiento</p>
            <p className="text-base text-gray-900 dark:text-white">
              {formatDate(payment.dueDate)}
            </p>
          </div>
          {payment.paidDate && (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700">
              <p className="text-sm text-green-700 dark:text-green-300 mb-1">Fecha de pago</p>
              <p className="text-base text-green-900 dark:text-green-100">
                {formatDate(payment.paidDate)}
              </p>
            </div>
          )}
        </div>

        {/* Invoice */}
        {payment.invoice && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Factura</p>
            <p className="text-base text-gray-900 dark:text-white break-all">{payment.invoice}</p>
          </div>
        )}
      </div>
    </SideModal>
  );
};

const AdminPaymentsPage: React.FC = () => {
  const { t } = useTranslation();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [searchEmail, setSearchEmail] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal de detalles
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const hasFetchedPayments = useRef(false);

  // Cargar pagos
  useEffect(() => {
    if (hasFetchedPayments.current) return;
    hasFetchedPayments.current = true;

    const fetchPayments = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await adminService.getPayments({
          status: filterStatus !== 'all' ? filterStatus : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        });

        if (response.success === false) {
          setError(response.error as string);
          setPayments([]);
        } else {
          setPayments(response.payments || []);
        }
      } catch (err) {
        console.error('Error fetching payments:', err);
        setError('Error al cargar los pagos');
        setPayments([]);
      } finally {
        setLoading(false);
        hasFetchedPayments.current = false;
      }
    };

    fetchPayments();
  }, [filterStatus, dateFrom, dateTo]);

  // Filtrar por email
  const filteredPayments = payments.filter((payment) => {
    if (!searchEmail) return true;
    return payment.tenantId?.toLowerCase().includes(searchEmail.toLowerCase());
  });

  // Paginar
  const totalPages = Math.ceil(filteredPayments.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedPayments = filteredPayments.slice(startIndex, startIndex + pageSize);

  // Calcular totales
  const totalPayments = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const paidPayments = filteredPayments
    .filter((p) => p.status === 'PAID')
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  const pendingPayments = filteredPayments
    .filter((p) => p.status === 'PENDING')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  // Marcar pago como pagado
  const handleMarkPaid = async (paymentId: string) => {
    try {
      const response = await adminService.markPaymentPaid(paymentId);
      if (response.success) {
        // Recargar pagos
        hasFetchedPayments.current = false;
        setLoading(true);
        const newResponse = await adminService.getPayments({
          status: filterStatus !== 'all' ? filterStatus : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        });
        setPayments(newResponse.payments || []);
        setLoading(false);
      } else {
        setError('Error al marcar el pago como pagado');
      }
    } catch (err) {
      console.error('Error marking payment as paid:', err);
      setError('Error al marcar el pago como pagado');
    }
  };

  const handleViewDetails = (payment: Payment) => {
    setSelectedPayment(payment);
    setShowDetailsModal(true);
  };

  const handleRefresh = () => {
    hasFetchedPayments.current = false;
    setCurrentPage(1);
    window.location.reload();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Pagos</h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          Gestión de pagos de clientes
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Total */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total en pagos</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ${totalPayments.toFixed(2)}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
              <DollarOutlined className="text-2xl text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        {/* Pagados */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Pagados</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                ${paidPayments.toFixed(2)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
              <CheckCircleOutlined className="text-2xl text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        {/* Pendientes */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Pendientes</p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                ${pendingPayments.toFixed(2)}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center">
              <ClockCircleOutlined className="text-2xl text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Buscar por email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Buscar por cliente
            </label>
            <div className="relative">
              <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Email o ID de cliente"
                value={searchEmail}
                onChange={(e) => {
                  setSearchEmail(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Estado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Estado
            </label>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
                hasFetchedPayments.current = false;
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">Todos</option>
              <option value="PENDING">Pendiente</option>
              <option value="PAID">Pagado</option>
              <option value="FAILED">Fallido</option>
              <option value="OVERDUE">Vencido</option>
            </select>
          </div>

          {/* Desde */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Desde
            </label>
            <div className="relative">
              <CalendarOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setCurrentPage(1);
                  hasFetchedPayments.current = false;
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Hasta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Hasta
            </label>
            <div className="relative">
              <CalendarOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setCurrentPage(1);
                  hasFetchedPayments.current = false;
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Botón de actualizar */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleRefresh}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 text-gray-700 dark:text-gray-300"
          >
            <ReloadOutlined />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Pagos List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Cargando pagos...</p>
        </div>
      ) : paginatedPayments.length === 0 ? (
        <EmptyState
          title="No hay pagos"
          description={
            searchEmail || filterStatus !== 'all' || dateFrom || dateTo
              ? 'No se encontraron pagos que coincidan con tus filtros'
              : 'No hay pagos para mostrar'
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {paginatedPayments.map((payment) => (
              <PaymentCard
                key={payment.paymentId}
                payment={payment}
                onMarkPaid={handleMarkPaid}
                onViewDetails={handleViewDetails}
                isLoading={loading}
              />
            ))}
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Mostrando {startIndex + 1} a {Math.min(startIndex + pageSize, filteredPayments.length)} de{' '}
                {filteredPayments.length} pagos
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <LeftOutlined />
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded-lg transition-colors ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <RightOutlined />
                </button>
              </div>

              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="5">5 por página</option>
                <option value="10">10 por página</option>
                <option value="25">25 por página</option>
                <option value="50">50 por página</option>
              </select>
            </div>
          )}
        </>
      )}

      {/* Payment Details Modal */}
      <PaymentDetailsModal
        payment={selectedPayment}
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
      />
    </div>
  );
};

export default AdminPaymentsPage;
